import { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, Button, message, Flex } from "antd";
import { SelectOption } from "@/types/select";
import { useCreateUser, useGetRoles } from "../../hooks/useUser";
import { CreateUserFormValues } from "@/types/user";
import { capitalize } from "@/utils/text";

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch: () => void;
}

const CreateModal = ({ isOpen, onClose, onRefetch }: CreateModalProps) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const passwordStrengthRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

  const { mutate: createUser } = useCreateUser();
  const { data: roles } = useGetRoles({
    params: {
      limit: -1,
    },
  });

  const roleOptions: SelectOption[] = useMemo(() => {
    return (
      roles?.map((role) => ({
        label: capitalize(role.name),
        value: role.id.toString(),
      })) || []
    );
  }, [roles]);

  const handleSubmit = async (values: CreateUserFormValues) => {
    setSubmitting(true);
    createUser(values, {
      onSuccess: () => {
        form.resetFields();
        onClose();
        message.success("Successfully created user");
        onRefetch();
        setSubmitting(false);
      },
      onError: () => {
        setSubmitting(false);
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title="Create User"
      footer={null}
      centered
      destroyOnHidden
    >
      <Form<CreateUserFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="username"
          label="Username"
          rules={[{ required: true, message: "Please enter the username" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter the email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[
            { required: true, message: "Please enter the password" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (value.length < 8 || !passwordStrengthRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.Password />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label="Confirm Password"
          dependencies={["password"]}
          rules={[
            {
              required: true,
              message: "Please confirm new password",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    "New password and confirm new password do not match"
                  )
                );
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>

        <Form.Item
          name="role_ids"
          label="Roles"
          rules={[
            { required: true, message: "Please select at least one role" },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select roles"
            options={roleOptions}
          />
        </Form.Item>

        <Flex justify="flex-end" gap={16} align="center">
          <Button onClick={onClose} variant="filled">
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            loading={submitting}
            variant="solid"
            color="primary"
          >
            Create
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default CreateModal;
